import React from 'react';
import useIsMobile from '../utils/useIsMobile';

const About = () => {
  const isMobile = useIsMobile();
  const achievements = [
    {
      number: '10+',
      label: 'Years in Business',
      description: 'Serving the lighting industry with excellence'
    },
    {
      number: '50K+',
      label: 'Happy Customers',
      description: 'Trusted by retailers and distributors nationwide'
    },
    {
      number: '100+',
      label: 'Product Range',
      description: 'Comprehensive lighting solutions'
    },
    {
      number: '24/7',
      label: 'Support',
      description: 'Round-the-clock customer assistance'
    }
  ];

  const timeline = [
    {
      year: '2014',
      title: 'Company Founded',
      description: 'Started with a vision to revolutionize LED lighting in India'
    },
    {
      year: '2016',
      title: 'First Manufacturing Unit',
      description: 'Established our first state-of-the-art manufacturing facility'
    },
    {
      year: '2018',
      title: 'National Expansion',
      description: 'Expanded distribution network across major cities'
    },
    {
      year: '2020',
      title: 'Innovation Award',
      description: 'Received national award for energy-efficient lighting solutions'
    },
    {
      year: '2022',
      title: 'International Markets',
      description: 'Began exporting to neighboring countries'
    },
    {
      year: '2024',
      title: 'Market Leadership',
      description: 'Established as one of the leading LED lighting brands'
    }
  ];

  const values = [
    {
      icon: '🎯',
      title: 'Quality First',
      description: 'We never compromise on quality. Every product undergoes rigorous testing to ensure it meets international standards.'
    },
    {
      icon: '🌱',
      title: 'Sustainability',
      description: 'Committed to eco-friendly solutions that reduce carbon footprint and promote sustainable development.'
    },
    {
      icon: '💡',
      title: 'Innovation',
      description: 'Continuously investing in R&D to bring cutting-edge lighting technology to our customers.'
    },
    {
      icon: '🤝',
      title: 'Customer Centric',
      description: 'Our customers are at the heart of everything we do. We build lasting relationships through trust and reliability.'
    },
    {
      icon: '⚡',
      title: 'Efficiency',
      description: 'Delivering energy-efficient solutions that help businesses reduce costs and improve productivity.'
    },
    {
      icon: '🏆',
      title: 'Excellence',
      description: 'Striving for excellence in every aspect of our business, from product design to customer service.'
    }
  ];

  return (
    <div>
      {/* Header */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1 style={{ fontSize: isMobile ? '2rem' : '3.5rem' }}>About FACTO ELECTRONICAL</h1>
            <p style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>Illuminating the future with premium LED solutions</p>
            <p style={{ fontSize: isMobile ? '0.8rem' : '1rem', color: '#999', marginTop: '1rem' }}>
              Your trusted partner in lighting innovation since 2014
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section>
        <div className="container">
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
            gap: isMobile ? '1.5rem' : '3rem',
            alignItems: 'center'
          }}>
            <div>
                <h2 style={{ color: 'var(--accent)', marginBottom: '1.5rem', fontSize: isMobile ? '1.5rem' : '2rem' }}>
                Our Story
              </h2>
              <p style={{ lineHeight: '1.8', color: '#ccc', marginBottom: '1.5rem' }}>
                FACTO ELECTRONICAL began its journey in 2014 with a simple yet powerful vision: 
                to transform the lighting industry in India with innovative, energy-efficient LED solutions. 
                Founded by a team of passionate engineers and lighting experts, we set out to address 
                the growing need for sustainable and high-quality lighting products.
              </p>
              <p style={{ lineHeight: '1.8', color: '#ccc', marginBottom: '1.5rem' }}>
                Over the years, we have grown from a small startup to one of the most trusted names 
                in the LED lighting industry. Our commitment to quality, innovation, and customer 
                satisfaction has helped us build strong relationships with retailers, distributors, 
                and end-users across the country.
              </p>
              <p style={{ lineHeight: '1.8', color: '#ccc' }}>
                Today, FACTO ELECTRONICAL stands as a testament to what can be achieved when 
                passion meets expertise. We continue to push the boundaries of lighting technology, 
                ensuring that our customers always have access to the best and most innovative 
                lighting solutions available in the market.
              </p>
            </div>
            
            <div>
              <img 
                src="/mobile%20hero/mobile%20hero%20banner.png"
                alt="FACTO ELECTRONICAL"
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  aspectRatio: '4/3',
                  objectFit: 'contain',
                  borderRadius: '12px'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section style={{ backgroundColor: 'var(--secondary)' }}>
        <div className="container">
          <div className="grid grid-2">
            <div className="card">
              <h3 style={{ color: 'var(--accent)', marginBottom: '1.5rem', textAlign: 'center' }}>
                Our Mission
              </h3>
              <p style={{ lineHeight: '1.8', color: '#ccc', textAlign: 'center' }}>
                To provide innovative, energy-efficient, and sustainable lighting solutions 
                that enhance spaces, reduce environmental impact, and deliver exceptional value 
                to our customers. We are committed to pushing the boundaries of LED technology 
                while maintaining the highest standards of quality and reliability.
              </p>
            </div>
            
            <div className="card">
              <h3 style={{ color: 'var(--accent)', marginBottom: '1.5rem', textAlign: 'center' }}>
                Our Vision
              </h3>
              <p style={{ lineHeight: '1.8', color: '#ccc', textAlign: 'center' }}>
                To be the leading provider of LED lighting solutions in India, recognized for 
                our innovation, quality, and customer-centric approach. We aim to illuminate 
                every space with smart, sustainable, and beautiful lighting solutions that 
                improve lives and protect our planet for future generations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section>
        <div className="container">
          <h2 className="section-title">Our Achievements</h2>
          <p className="section-subtitle">Milestones that mark our journey of excellence</p>
          
          <div className="grid grid-4">
            {achievements.map((achievement, index) => (
              <div key={index} className="card" style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: isMobile ? '1.8rem' : '2.5rem', 
                  color: 'var(--accent)', 
                  fontWeight: 'bold',
                  marginBottom: '0.5rem'
                }}>
                  {achievement.number}
                </div>
                <h4 style={{ color: 'var(--accent)', marginBottom: '0.5rem' }}>
                  {achievement.label}
                </h4>
                <p style={{ fontSize: '0.9rem', color: '#ccc' }}>
                  {achievement.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section style={{ backgroundColor: 'var(--secondary)' }}>
        <div className="container">
          <h2 className="section-title">Our Journey</h2>
          <p className="section-subtitle">Key milestones in our growth story</p>
          
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {timeline.map((item, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '0.75rem' : '2rem',
                marginBottom: '1.5rem',
                position: 'relative'
              }}>
                <div style={{ 
                  minWidth: isMobile ? 'auto' : '80px', 
                  textAlign: 'center',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isMobile ? 'flex-start' : 'center',
                  gap: isMobile ? '0.75rem' : '0'
                }}>
                  <div style={{
                    width: isMobile ? '40px' : '60px',
                    height: isMobile ? '40px' : '60px',
                    backgroundColor: 'var(--accent)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--primary)',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '0.85rem' : '1.1rem',
                    flexShrink: 0
                  }}>
                    {item.year}
                  </div>
                  {isMobile && (
                    <h4 style={{ color: 'var(--accent)', margin: 0, fontSize: '1rem' }}>
                      {item.title}
                    </h4>
                  )}
                  {index < timeline.length - 1 && !isMobile && (
                    <div style={{
                      position: 'absolute',
                      top: '60px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '2px',
                      height: '60px',
                      backgroundColor: 'var(--accent)'
                    }}></div>
                  )}
                </div>
                
                <div className="card" style={{ flex: 1 }}>
                  {!isMobile && (
                    <h4 style={{ color: 'var(--accent)', marginBottom: '0.5rem' }}>
                      {item.title}
                    </h4>
                  )}
                  <p style={{ color: '#ccc', lineHeight: '1.6' }}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section>
        <div className="container">
          <h2 className="section-title">Our Core Values</h2>
          <p className="section-subtitle">Principles that guide everything we do</p>
          
          <div className="grid grid-3">
            {values.map((value, index) => (
              <div key={index} className="card">
                <div style={{ 
                  fontSize: '2.5rem', 
                  textAlign: 'center',
                  marginBottom: '1rem'
                }}>
                  {value.icon}
                </div>
                <h3 style={{ color: 'var(--accent)', textAlign: 'center', marginBottom: '1rem' }}>
                  {value.title}
                </h3>
                <p style={{ color: '#ccc', lineHeight: '1.6', textAlign: 'center' }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section style={{ backgroundColor: 'var(--secondary)' }}>
        <div className="container">
          <h2 className="section-title">Leadership Team</h2>
          <p className="section-subtitle">Meet the people behind our success</p>
          
          <div className="grid grid-3">
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '120px', 
                height: '120px', 
                backgroundColor: 'var(--accent)', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '3rem',
                color: 'var(--primary)'
              }}>
                👔
              </div>
              <h4 style={{ color: 'var(--accent)', marginBottom: '0.5rem' }}>
                Rajesh Kumar
              </h4>
              <p style={{ color: '#999', marginBottom: '0.5rem' }}>
                Founder & CEO
              </p>
              <p style={{ fontSize: '0.9rem', color: '#ccc' }}>
                Visionary leader with 20+ years of experience in the lighting industry
              </p>
            </div>
            
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '120px', 
                height: '120px', 
                backgroundColor: 'var(--accent)', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '3rem',
                color: 'var(--primary)'
              }}>
                👩‍💼
              </div>
              <h4 style={{ color: 'var(--accent)', marginBottom: '0.5rem' }}>
                Priya Sharma
              </h4>
              <p style={{ color: '#999', marginBottom: '0.5rem' }}>
                COO
              </p>
              <p style={{ fontSize: '0.9rem', color: '#ccc' }}>
                Operations expert ensuring smooth and efficient business processes
              </p>
            </div>
            
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '120px', 
                height: '120px', 
                backgroundColor: 'var(--accent)', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '3rem',
                color: 'var(--primary)'
              }}>
                👨‍🔬
              </div>
              <h4 style={{ color: 'var(--accent)', marginBottom: '0.5rem' }}>
                Dr. Amit Patel
              </h4>
              <p style={{ color: '#999', marginBottom: '0.5rem' }}>
                Head of R&D
              </p>
              <p style={{ fontSize: '0.9rem', color: '#ccc' }}>
                Innovation leader driving cutting-edge lighting technology
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h2>Join Our Success Story</h2>
            <p>Partner with us and experience the FACTO ELECTRONICAL difference</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn">Contact Us</button>
              <button className="btn-secondary">View Products</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
